import './forgot-password-modal.css';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { useAuth } from '../../hooks/use-auth';
import { useToasts } from '../../hooks/use-toasts';
import { isValidEmail } from '../../utils/validators';
import { InputText } from '../../components/ui/input-text/input-text';

interface ForgotPasswordModalProps {
  triggerText?: string;
}

export const ForgotPasswordModal = ({ triggerText = 'Forgot password?' }: ForgotPasswordModalProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | boolean>(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { resetPassword } = useAuth();
  const { showToast } = useToasts();

  const handleClose = () => setOpen(false);

  const handleOpen = () => setOpen(true);

  const onEmailChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(event.target.value);
  };

  useEffect(() => {
    if (open) {
      setError(false);
      setEmail('');
      setHasSubmitted(false);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (hasSubmitted) validateEmail();
  }, [email]);

  const validateEmail = () => {
    let error: boolean | string = false;
    if (!isValidEmail(email)) error = 'Invalid email.';
    setError(error);
  };

  const onResetClick = async () => {
    setHasSubmitted(true);
    validateEmail();
    if (isValidEmail(email)) {
      setLoading(true);
      try {
        await resetPassword(email);
        showToast({
          severity: 'success',
          summary: 'Please check your inbox.',
          detail: "We've sent you instructions to reset your password.",
          sticky: true,
        });
        handleClose();
      } catch (error: any) {
        if (!error) error = { message: 'An unknown error occured.' };
        showToast({
          severity: 'error',
          summary: error.message || `An unknown error occured [${error.code}].`,
          detail: `Error code [${error.code || 'unknown'}]`,
          sticky: true,
        });
      }
      setLoading(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" className="p-button-secondary" onClick={handleClose} disabled={loading} />
      <Button label="Reset password" onClick={onResetClick} loading={loading} disabled={loading} />
    </div>
  );

  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });

  return (
    <>
      <Button label={triggerText} className="p-button-link" onClick={handleOpen} />
      <Dialog
        header="Reset Password"
        footer={footer}
        breakpoints={{ '960px': '75vw', '640px': '100vw' }}
        resizable={false}
        maximized={isMobile}
        draggable={false}
        dismissableMask={true}
        visible={open}
        style={{ width: '50vw', ...(isMobile ? { borderRadius: 0 } : {}) }}
        onHide={handleClose}
        headerStyle={isMobile ? { borderRadius: 0 } : {}}
      >
        <p>We will send you instructions to reset your password to your Email address.</p>
        <InputText
          name="email"
          onChange={onEmailChange}
          value={email}
          iconLeft="pi-user"
          placeholder="Email address"
          error={error}
        />
      </Dialog>
    </>
  );
};
